import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

import Header from './components/Header';
import Footer from './components/Footer';
import ArticleDetail from './components/ArticleDetail';
import NosotrosPage from './components/NosotrosPage';
import HeroSection from './sections/HeroSection';
import ValuesSection from './sections/ValuesSection';
import ServicesSection from './sections/ServicesSection';
import KnowledgeSection from './sections/KnowledgeSection';
import EbooksSection from './sections/EbooksSection';
import NewsletterSection from './sections/NewsletterSection';

gsap.registerPlugin(ScrollTrigger);

// El sitio es una página con scroll más dos vistas propias, compartibles e
// indexables: /conocimiento/{slug} y /nosotros. "Nosotros" era un ancla del
// scroll; volvió a ser página, como en el sitio anterior, porque es lo que la
// gente comparte y lo que espera del menú.
export const parsePath = () => {
  const path = window.location.pathname;
  const m = path.match(/^\/conocimiento\/([\w-]+)\/?$/);
  if (m) return { slug: m[1], page: null };
  if (/^\/nosotros\/?$/.test(path)) return { slug: null, page: 'nosotros' };
  return { slug: null, page: null };
};

export default function App() {
  const [route, setRoute] = useState(parsePath);
  const lenisRef = useRef(null);

  // Lenis es dueño del bucle de scroll y ScrollTrigger se actualiza desde él.
  // Este es el único punto de unión entre los dos, igual que en la referencia.
  useEffect(() => {
    const lenis = new Lenis({
      lerp: 0.1,
      smoothWheel: true,
    });
    lenisRef.current = lenis;

    lenis.on('scroll', ScrollTrigger.update);

    const raf = (time) => { lenis.raf(time * 1000); };
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  // Sincroniza el estado con el botón atrás/adelante del navegador.
  useEffect(() => {
    const onPopState = () => setRoute(parsePath());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // -80 = alto del header fijo. Lenis resuelve el destino en el momento de la
  // llamada, así que si la maquetación aún está creciendo (fuentes, imágenes)
  // se queda corto.
  const scrollToSection = (destino) => {
    const el = document.getElementById(destino);
    if (!el) return false;
    if (lenisRef.current) {
      lenisRef.current.scrollTo(el, { offset: -80, duration: 1.2 });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    return true;
  };

  // Volver de un artículo a una sección es el caso frágil: la home se acaba de
  // montar y su altura todavía crece mientras cargan tipografías e imágenes, así
  // que el primer scrollTo apunta a una posición que deja de ser válida a mitad
  // de la animación (se quedaba a más de 3000px del destino). Se espera a que las
  // fuentes estén listas y luego se corrige hasta que el objetivo deja de moverse.
  // El salto es instantáneo a propósito: se viene de otra vista, así que animar
  // no aporta, y una animación de 1.2s reabriría la carrera —cada corrección
  // reiniciaría el tween y nunca convergería.
  const scrollToSectionCuandoAsiente = (destino) => {
    let intentos = 0;
    const corregir = () => {
      const el = document.getElementById(destino);
      if (!el) {
        if (++intentos < 12) setTimeout(corregir, 100);
        return;
      }
      // Tras el salto la sección debe quedar a 80px del borde superior: eso es
      // lo que significa `offset: -80` en Lenis.
      const desviacion = Math.abs(el.getBoundingClientRect().top - 80);
      if (desviacion > 4) {
        // Destino numérico, no `scrollTo(el, { offset: -80 })`: con
        // `immediate: true` Lenis ignora el offset y deja la sección pegada al
        // borde, bajo el header. Con un número no hay ambigüedad.
        const objetivo = window.scrollY + el.getBoundingClientRect().top - 80;
        if (lenisRef.current) {
          lenisRef.current.scrollTo(objetivo, { immediate: true });
        } else {
          window.scrollTo({ top: objetivo });
        }
      }
      if (++intentos < 12) setTimeout(corregir, 200);
    };
    const arrancar = () => requestAnimationFrame(corregir);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(arrancar, arrancar);
    } else {
      arrancar();
    }
  };

  // Entrar directamente en /#contacto —desde el 301 de /newsletter/, desde un
  // enlace compartido o desde un correo— tiene que llevar a la sección. El
  // navegador NO puede hacerlo solo: el HTML que sirve el servidor es un
  // <div id="root"> vacío, así que cuando procesa el fragmento el elemento
  // todavía no existe; lo pinta React después. Firefox reintenta el anclaje
  // cuando el elemento aparece, pero Chrome y Safari no, y en Safari y en móvil
  // el enlace dejaba al usuario arriba del todo.
  useEffect(() => {
    if (route.slug || route.page) return;
    const id = window.location.hash.slice(1);
    if (!id) return;
    // Enlaces viejos a /#nosotros: ese ancla ya no existe en la home. En vez de
    // dejar al visitante arriba del todo sin explicación, se reescribe la URL a
    // la página nueva.
    if (id === 'nosotros') {
      window.history.replaceState({}, '', '/nosotros');
      setRoute({ slug: null, page: 'nosotros' });
      return;
    }
    scrollToSectionCuandoAsiente(id);
    // Solo al montar: después de esto navegar ya es cosa de navigate().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cambiar de vista cambia la altura de la página de golpe. Sin esto,
  // ScrollTrigger conserva las medidas de la vista anterior y las animaciones
  // de la nueva disparan a destiempo.
  useEffect(() => {
    const id = requestAnimationFrame(() => ScrollTrigger.refresh());
    return () => cancelAnimationFrame(id);
  }, [route.slug, route.page]);

  const enHome = route.slug === null && route.page === null;

  const openNosotros = () => {
    window.history.pushState({}, '', '/nosotros');
    setRoute({ slug: null, page: 'nosotros' });
    // Instantáneo, no suave: se viene de otra vista, animar el scroll de una
    // página que acaba de montarse no aporta.
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    else window.scrollTo({ top: 0 });
  };

  // Navegar es casi siempre desplazarse a un ancla. Se conserva pushState para
  // que la URL refleje la sección y el enlace se pueda compartir.
  const navigate = (p) => {
    const destino = p === 'home' ? 'hero' : p;

    // Nosotros dejó de ser un ancla del scroll: es una vista.
    if (destino === 'nosotros') {
      openNosotros();
      return;
    }

    // Si el destino existe en la vista actual es un ancla local. Pasa con
    // "Contacto" desde /nosotros: esa página lleva su propia sección de
    // newsletter, así que no hay motivo para volver a la home.
    if (enHome || document.getElementById(destino)) {
      window.history.pushState({}, '', enHome && destino === 'hero' ? '/' : `#${destino}`);
      if (destino === 'hero') {
        if (lenisRef.current) lenisRef.current.scrollTo(0, { duration: 1.2 });
        else window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      scrollToSection(destino);
      return;
    }

    // Estando fuera de la home y sin ese ancla aquí: se vuelve y se salta a la
    // sección cuando ya está montada y la maquetación ha asentado.
    window.history.pushState({}, '', destino === 'hero' ? '/' : `/#${destino}`);
    setRoute({ slug: null, page: null });
    if (destino === 'hero') {
      window.scrollTo({ top: 0 });
      return;
    }
    scrollToSectionCuandoAsiente(destino);
  };

  const openArticle = (slug) => {
    window.history.pushState({}, '', `/conocimiento/${slug}`);
    setRoute({ slug, page: null });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const backToConocimiento = () => navigate('conocimiento');

  return (
    <div className="relative bg-[#0a0e14]">
      <Header onNavigate={navigate} />
      {route.slug ? (
        <main>
          <ArticleDetail slug={route.slug} onBack={backToConocimiento} />
        </main>
      ) : route.page === 'nosotros' ? (
        <main>
          <NosotrosPage onNavigate={navigate} />
        </main>
      ) : (
        <main>
          <HeroSection />
          <ValuesSection />
          <ServicesSection />
          <KnowledgeSection openArticle={openArticle} />
          <EbooksSection />
          <NewsletterSection />
        </main>
      )}
      <Footer onNavigate={navigate} />
    </div>
  );
}
